// ============================================================================
// components/mail/MailInboxScreen.tsx — ЭКРАН «ПОЧТА: ВХОДЯЩИЕ».
//
// Три области (desktop): ЯЩИКИ+ПАПКИ → СПИСОК ПИСЕМ → ЧТЕНИЕ ПИСЬМА.
// На мобильном — один столбец: список и чтение по очереди.
//
// Ящики — MailService (lib/mail/mailData, доступ/изоляция на сервере);
// письма и папки — существующий RPC MetadataService.ExecuteHydraql через
// lib/mail/mailData (серверное почтовое ядро internal/mail). Новых proto,
// серверных методов и прав не требуется. Живое обновление — шина (mail.*),
// без поллинга.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mail, RefreshCw, Search, X } from 'lucide-react';
import { eventBusHub } from '@/lib/eventBusHub';
import { friendlyError } from '@/lib/errors';
import { createLogger } from '@/lib/logger';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useToastStore } from '@/store/toastStore';
import {
  buildFolderViews,
  filterMessages,
  folderLabel,
  loadMailAccounts,
  loadMailAttachments,
  loadMailFolders,
  loadMailMessageDetail,
  loadMailMessages,
  MAIL_WINDOW_LIMIT,
  type MailAccount,
  type MailAttachmentMeta,
  type MailFolder,
  type MailMessage,
  type MailMessageDetail,
} from '@/lib/mail/mailData';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MailSidebar } from '@/components/mail/MailSidebar';
import { MailMessageList } from '@/components/mail/MailMessageList';
import { MailMessageView } from '@/components/mail/MailMessageView';

const log = createLogger('MailInbox');

const INBOX = 'INBOX';

export function MailInboxScreen() {
  const { isMobile } = useBreakpoint();
  const showError = useToastStore((state) => state.showError);

  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [accountId, setAccountId] = useState('');
  const [folders, setFolders] = useState<MailFolder[]>([]);
  const [messages, setMessages] = useState<MailMessage[]>([]);
  const [folderKey, setFolderKey] = useState(INBOX);
  const [query, setQuery] = useState('');

  const [selected, setSelected] = useState<MailMessage | null>(null);
  const [detail, setDetail] = useState<MailMessageDetail | null>(null);
  const [attachments, setAttachments] = useState<MailAttachmentMeta[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingMailbox, setLoadingMailbox] = useState(false);
  const [error, setError] = useState('');

  const accountRef = useRef('');
  const mailboxSeq = useRef(0);
  const detailSeq = useRef(0);

  useEffect(() => {
    accountRef.current = accountId;
  }, [accountId]);

  const selectAccount = useCallback((next: string) => {
    setAccountId(next);
    setFolderKey(INBOX);
    setSelected(null);
    setDetail(null);
    setAttachments([]);
    setQuery('');
  }, []);

  const reloadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const list = await loadMailAccounts();
      setAccounts(list);
      setError('');
      setAccountId((previous) => {
        if (previous && list.some((account) => account.id === previous)) return previous;
        return list.find((account) => account.enabled)?.id || list[0]?.id || '';
      });
    } catch (cause) {
      const message = friendlyError(cause);
      log.warn('Не удалось загрузить почтовые ящики', cause);
      setError(message);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  const loadMailbox = useCallback(async (id: string, silent: boolean) => {
    if (!id) {
      setFolders([]);
      setMessages([]);
      return;
    }
    const seq = ++mailboxSeq.current;
    if (!silent) setLoadingMailbox(true);
    try {
      const [folderRows, messageRows] = await Promise.all([
        loadMailFolders(id),
        loadMailMessages(id),
      ]);
      if (seq !== mailboxSeq.current) return;
      setFolders(folderRows);
      setMessages(messageRows);
      setError('');
    } catch (cause) {
      if (seq !== mailboxSeq.current) return;
      const message = friendlyError(cause);
      log.warn('Не удалось загрузить письма', cause);
      setError(message);
    } finally {
      if (seq === mailboxSeq.current) setLoadingMailbox(false);
    }
  }, []);

  useEffect(() => {
    void reloadAccounts();
  }, [reloadAccounts]);

  useEffect(() => {
    if (!accountId) {
      setFolders([]);
      setMessages([]);
      return;
    }
    setSelected(null);
    setDetail(null);
    setAttachments([]);
    void loadMailbox(accountId, false);
  }, [accountId, loadMailbox]);

  // Живое обновление: сервер публикует mail.queued|sent|failed|received.{id}.
  useEffect(() => eventBusHub.subscribe((event) => {
    if (!event.topic.startsWith('mail.')) return;
    const id = accountRef.current;
    if (id) void loadMailbox(id, true);
  }), [loadMailbox]);

  const refresh = useCallback(async () => {
    await reloadAccounts();
    const id = accountRef.current;
    if (id) await loadMailbox(id, true);
  }, [loadMailbox, reloadAccounts]);

  const openMessage = useCallback(async (message: MailMessage) => {
    setSelected(message);
    setDetail(null);
    setAttachments([]);
    setDetailLoading(true);
    const seq = ++detailSeq.current;
    try {
      const [messageDetail, messageAttachments] = await Promise.all([
        loadMailMessageDetail(message.id),
        loadMailAttachments(message.id),
      ]);
      if (seq !== detailSeq.current) return;
      setDetail(messageDetail);
      setAttachments(messageAttachments);
    } catch (cause) {
      if (seq !== detailSeq.current) return;
      log.warn('Не удалось прочитать письмо', { id: message.id, cause });
      showError(friendlyError(cause));
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false);
    }
  }, [showError]);

  const closeMessage = useCallback(() => {
    detailSeq.current += 1;
    setSelected(null);
    setDetail(null);
    setAttachments([]);
    setDetailLoading(false);
  }, []);

  const folderViews = useMemo(() => buildFolderViews(messages, folders), [messages, folders]);
  const visibleMessages = useMemo(
    () => filterMessages(messages, folderKey, query),
    [messages, folderKey, query],
  );
  const currentFolder = folderViews.find((folder) => folder.key === folderKey);
  const folderTitle = folderKey ? (currentFolder?.label || folderLabel(folderKey)) : 'Все письма';
  const searchActive = query.trim().length > 0;

  const searchBar = (
    <div className="relative">
      <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Поиск: тема, отправитель, текст"
        className="h-8 pl-8 pr-8 text-sm"
        aria-label="Поиск писем"
      />
      {searchActive && (
        <button
          type="button"
          onClick={() => setQuery('')}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
          title="Очистить поиск"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  if (loadingAccounts && accounts.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Загрузка почты…
      </div>
    );
  }

  if (!loadingAccounts && accounts.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <Mail className="h-10 w-10 text-muted-foreground/50" />
        <div className="text-base font-semibold">Почтовые ящики не подключены</div>
        <div className="max-w-md text-sm text-muted-foreground">
          Подключите ящик в профиле: Настройки → Подключения → Почтовые ящики. После этого папки и письма
          появятся здесь.
        </div>
        {error && <div className="text-xs text-destructive">{error}</div>}
        <Button size="sm" variant="outline" onClick={() => void refresh()}>
          <RefreshCw className="mr-1 h-4 w-4" />Обновить
        </Button>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 border-b px-3 py-2">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">Почта</div>
              <div className="truncate text-[11px] text-muted-foreground">
                {accounts.find((account) => account.id === accountId)?.email || 'Ящик не выбран'}
              </div>
            </div>
            <select
              value={accountId}
              onChange={(event) => selectAccount(event.target.value)}
              className="max-w-[45%] rounded-md border bg-background px-2 py-1 text-xs"
              aria-label="Почтовый ящик"
            >
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>{account.name}</option>
              ))}
            </select>
            <Button size="sm" variant="ghost" onClick={() => void refresh()} title="Обновить">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-2 flex gap-1 overflow-x-auto pb-1">
            {folderViews.map((folder) => (
              <button
                key={folder.key || '__all__'}
                type="button"
                onClick={() => { setFolderKey(folder.key || ''); closeMessage(); }}
                className={`shrink-0 rounded-full border px-2.5 py-1 text-xs ${
                  (folder.key || '') === folderKey ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground'
                }`}
              >
                {folder.label}
                {folder.unread > 0 && <span className="ml-1 font-semibold">{folder.unread}</span>}
              </button>
            ))}
          </div>
          {searchBar}
        </div>
        {error && <div className="border-b bg-destructive/10 px-3 py-1 text-xs text-destructive">{error}</div>}
        {selected ? (
          <div className="min-h-0 flex-1">
            <MailMessageView
              message={selected}
              detail={detail}
              attachments={attachments}
              loading={detailLoading}
              onBack={closeMessage}
            />
          </div>
        ) : (
          <MailMessageList
            messages={visibleMessages}
            selectedId=""
            onSelect={(message) => void openMessage(message)}
            loading={loadingMailbox}
            emptyText={searchActive ? 'Ничего не найдено' : 'В этой папке писем нет'}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
        <Mail className="h-5 w-5 text-primary" />
        <div>
          <div className="text-sm font-semibold leading-tight">Почта</div>
          <div className="text-[11px] leading-tight text-muted-foreground">Входящие</div>
        </div>
        <div className="ml-auto text-[11px] text-muted-foreground">
          поиск по последним {MAIL_WINDOW_LIMIT} письмам ящика
        </div>
        <Button size="sm" variant="outline" onClick={() => void refresh()} disabled={loadingMailbox}>
          <RefreshCw className={`mr-1 h-4 w-4 ${loadingMailbox ? 'animate-spin' : ''}`} />Обновить
        </Button>
      </div>
      {error && <div className="border-b bg-destructive/10 px-3 py-1 text-xs text-destructive">{error}</div>}
      <div className="flex min-h-0 flex-1">
        <aside className="flex w-56 shrink-0 flex-col border-r">
          <MailSidebar
            accounts={accounts}
            accountId={accountId}
            onSelectAccount={selectAccount}
            folders={folderViews}
            folderKey={folderKey}
            onSelectFolder={(key) => { setFolderKey(key); closeMessage(); }}
            loading={loadingAccounts}
          />
        </aside>
        <section className="flex w-[360px] shrink-0 flex-col border-r">
          <div className="shrink-0 space-y-1.5 border-b px-3 py-2">
            <div className="flex items-baseline gap-2">
              <h1 className="text-sm font-semibold">{folderTitle}</h1>
              <span className="text-[11px] text-muted-foreground">
                {visibleMessages.length}
                {searchActive ? ` из ${messages.length}` : ''}
              </span>
              {(currentFolder?.unread || 0) > 0 && (
                <span className="ml-auto text-[11px] font-medium text-primary">
                  непрочитанных: {currentFolder?.unread}
                </span>
              )}
            </div>
            {searchBar}
          </div>
          <MailMessageList
            messages={visibleMessages}
            selectedId={selected?.id || ''}
            onSelect={(message) => void openMessage(message)}
            loading={loadingMailbox}
            emptyText={searchActive ? 'Ничего не найдено' : 'В этой папке писем нет'}
          />
        </section>
        <section className="min-w-0 flex-1">
          <MailMessageView
            message={selected}
            detail={detail}
            attachments={attachments}
            loading={detailLoading}
          />
        </section>
      </div>
    </div>
  );
}
